import { rd, type Maybe, type RemoteData } from '@passionware/monads';
import type { Booking, Resource } from '__types__';
import type {
  BookingConfirmationPreview,
  BookingConfirmationPreviewRequest,
  BookingConfirmationTemplateScope
} from 'v5/api/booking/booking.api';
import type { EnterpriseWorkspace } from 'v5/api/enterprise/enterprise.api';
import type { ResolvedSetting } from 'v5/api/system-settings/system-settings.api';
import { appScopeUtils, type AppScope } from 'v5/core/app-scope/appScope.types';
import { appRouting } from 'v5/core/routing/appRouting';
import { MergeServices } from 'v5/platform/ts/services';
import { WithSystemSettingsService } from 'v5/services/internal/SystemSettingsService/SystemSettingsService';
import { WithBookingService } from 'v5/services/io/BookingService/BookingService';
import { WithEnterpriseService } from 'v5/services/io/EnterpriseService/EnterpriseService';
import {
  type ConfirmationRecipientList,
  recipientEmailsFromResource
} from './useProductionConfirmationRecipients';

/** What the dialog sends to: one booking, or the selected recipient of a production. */
export type ConfirmationPreviewTarget =
  | {
      kind: 'booking';
      bookingRd: RemoteData<Booking>;
      resourceRd: RemoteData<Resource>;
    }
  | {
      kind: 'production';
      recipientsRd: RemoteData<ConfirmationRecipientList>;
      selectedIndex: number;
    };

/**
 * The booking to preview, or undefined when no preview will be requested: the contact has no
 * email, or the production has no recipient ready to send to.
 * Here and below, idle means an input has not loaded yet ("wait"), never "no preview".
 */
export function previewBookingId(
  target: ConfirmationPreviewTarget
): RemoteData<Maybe<number>> {
  if (target.kind === 'production') {
    return rd.map(target.recipientsRd, ({ ready }) =>
      ready.length === 0
        ? undefined
        : ready[Math.min(target.selectedIndex, ready.length - 1)].bookingId
    );
  }
  return rd.mapMonadic(target.bookingRd, booking =>
    booking.contact_id == null
      ? rd.of(undefined)
      : rd.map(target.resourceRd, resource =>
          recipientEmailsFromResource(resource).length === 0
            ? undefined
            : booking.id
        )
  );
}

/** The URL workspace's id; undefined at organization scope. */
export function urlWorkspaceId(
  appScope: AppScope,
  workspaceRd: RemoteData<Maybe<EnterpriseWorkspace>>
): RemoteData<Maybe<number>> {
  return appScope.level === 'organization'
    ? rd.of(undefined)
    : rd.map(workspaceRd, workspace => workspace?.id);
}

/** The preview request, or undefined when none will be made. A workspace page needs its workspace. */
export function previewRequest(
  open: boolean,
  appScope: AppScope,
  bookingIdRd: RemoteData<Maybe<number>>,
  workspaceIdRd: RemoteData<Maybe<number>>
): RemoteData<Maybe<BookingConfirmationPreviewRequest>> {
  if (!open) {
    return rd.of(undefined);
  }
  return rd.mapMonadic(bookingIdRd, bookingId =>
    bookingId == null
      ? rd.of(undefined)
      : rd.map(workspaceIdRd, workspaceId =>
          appScope.level !== 'organization' && workspaceId == null
            ? undefined
            : { bookingId, enterpriseWorkspaceId: workspaceId }
        )
  );
}

/** The template the URL suggests: its workspace, and whether that workspace has one of its own. */
export function urlTemplateGuess(
  workspaceIdRd: RemoteData<Maybe<number>>,
  settingRd: RemoteData<Pick<ResolvedSetting, 'origin'>>
): RemoteData<BookingConfirmationTemplateScope> {
  return rd.mapMonadic(workspaceIdRd, workspaceId =>
    rd.map(settingRd, setting => ({
      workspaceId: workspaceId ?? null,
      origin: setting.origin === 'workspace' ? 'workspace' : 'global'
    }))
  );
}

/**
 * The template the send dialog describes. The backend reports the one it rendered (the
 * production's workspace wins over the URL), so this waits for the preview. The URL guess is used
 * only when no preview will be requested, it fails, or the backend does not report the template.
 */
export function resolveConfirmationTemplate(
  requestRd: RemoteData<Maybe<BookingConfirmationPreviewRequest>>,
  previewRd: RemoteData<BookingConfirmationPreview>,
  urlGuessRd: RemoteData<BookingConfirmationTemplateScope>
): RemoteData<BookingConfirmationTemplateScope> {
  return rd.mapMonadic(requestRd, request =>
    request == null || rd.isError(previewRd)
      ? urlGuessRd
      : rd.mapMonadic(previewRd, preview =>
          preview.template ? rd.of(preview.template) : urlGuessRd
        )
  );
}

export type ConfirmationTemplateScope = BookingConfirmationTemplateScope & {
  /** The workspace whose template was looked up; absent for the organization or when it failed to load. */
  workspace: Maybe<EnterpriseWorkspace>;
  /** Where to edit that workspace's template; null for the organization. */
  templatesPath: string | null;
};

/** The confirmation preview for the send dialog and the template it was rendered from. */
export function useConfirmationPreview(
  {
    open,
    appScope,
    target
  }: { open: boolean; appScope: AppScope; target: ConfirmationPreviewTarget },
  services: MergeServices<
    [WithBookingService, WithSystemSettingsService, WithEnterpriseService]
  >
) {
  const { organizationId } = appScope;

  const workspaceIdRd = urlWorkspaceId(
    appScope,
    services.enterpriseService.useWorkspaceBySlug(
      organizationId,
      appScopeUtils.getWorkspaceSlug(appScope)
    )
  );
  const previewRequestRd = previewRequest(
    open,
    appScope,
    previewBookingId(target),
    workspaceIdRd
  );
  const previewRd = rd.useLastWithPlaceholder(
    services.bookingService.useBookingConfirmationPreview(
      rd.tryGet(previewRequestRd)
    )
  );

  const urlSettingRd = services.systemSettingsService.useSetting(
    rd.tryMap(workspaceIdRd, workspaceId =>
      appScopeUtils.identityFromEntityWorkspace(
        organizationId,
        workspaceId ?? null
      )
    ),
    'defaults_booking_confirmation'
  );
  const templateRd = resolveConfirmationTemplate(
    previewRequestRd,
    previewRd,
    urlTemplateGuess(workspaceIdRd, urlSettingRd)
  );

  const templateWorkspaceId = rd.tryGet(templateRd)?.workspaceId;
  const templateWorkspaceRd = services.enterpriseService.useWorkspace(
    organizationId,
    templateWorkspaceId
  );
  const templateScopeRd = rd.useMemoMap(
    rd.useCombine({
      // combine reads idle beside success as an error; here idle still means "wait"
      template: rd.isIdle(templateRd) ? rd.ofPending() : templateRd,
      // wait for the name as well, so the banner does not change once shown
      workspace:
        templateWorkspaceId == null || rd.isError(templateWorkspaceRd)
          ? rd.of(null)
          : rd.isIdle(templateWorkspaceRd)
            ? rd.ofPending()
            : templateWorkspaceRd
    }),
    ({ template, workspace }): ConfirmationTemplateScope => ({
      ...template,
      workspace,
      templatesPath: workspace
        ? appRouting
            .org(organizationId)
            .settings.workspace(workspace.slug)
            .confirmationTemplates()
        : null
    })
  );

  return {
    /** Undefined until there is someone to send to on the right workspace. */
    previewRequest: rd.tryGet(previewRequestRd),
    previewRd,
    templateScopeRd
  };
}
